import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download } from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api, { BACKEND_URL } from '../../lib/api';

const BalanceSheet = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [asOfDate, setAsOfDate] = useState('');

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
    fetchBS();
  }, [navigate]);

  const fetchBS = async () => {
    setLoading(true);
    try {
      const params = asOfDate ? `?as_of_date=${asOfDate}` : '';
      const r = await api.get(`/accounts/balance-sheet${params}`);
      setData(r.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const exportPDF = () => {
    const params = asOfDate ? `?as_of_date=${asOfDate}` : '';
    const token = localStorage.getItem('token');
    window.open(`${BACKEND_URL}/api/accounts/export/balance-sheet${params}&token=${token}`, '_blank');
  };

  const Section = ({ title, items, total, totalLabel, color }) => (
    <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm">
      <div className="px-4 py-3 border-b border-[#e5e7eb]" style={{ borderLeftWidth: 4, borderLeftColor: color }}><h2 className="font-semibold">{title}</h2></div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[400px]">
        <tbody>
          {items.length === 0 ? <tr><td className="py-4 px-4 text-center text-[#6b7280]" colSpan="2">No items</td></tr> : items.map((a, i) => (
            <tr key={i} className="border-t border-[#e5e7eb]"><td className="py-2 px-4">{a.code} - {a.name}</td><td className="py-2 px-4 text-right font-medium">Rs. {a.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
          ))}
        </tbody>
        <tfoot className="bg-[#f9fafb] font-semibold"><tr><td className="py-2 px-4">{totalLabel}</td><td className="py-2 px-4 text-right">Rs. {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr></tfoot>
        </table>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="balance-sheet-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <div className="mb-6 flex justify-between items-center">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1a1a2e]" data-testid="bs-title">Balance Sheet</h1>
            {data && <button onClick={exportPDF} className="flex items-center gap-1 text-[#1E40AF] font-medium hover:underline" data-testid="export-bs-pdf"><Download className="w-4 h-4" /> Export PDF</button>}
          </div>

          <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
              <div><label className="block text-sm font-medium mb-1">As of Date</label><input type="date" className="w-full border border-[#e5e7eb] rounded-sm px-3 py-2" value={asOfDate} onChange={e => setAsOfDate(e.target.value)} data-testid="bs-date" /></div>
              <button onClick={fetchBS} className="bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white hover:opacity-90 rounded-sm px-4 py-2 font-medium" data-testid="fetch-bs-button">Refresh</button>
            </div>
          </div>

          {loading ? <div className="text-center py-12">Loading...</div> : !data ? (
            <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-12 text-center text-[#6b7280]">No data</div>
          ) : (
            <div className="space-y-4">
              <Section title="Assets" items={data.assets} total={data.total_assets} totalLabel="Total Assets" color="#1E40AF" />
              <Section title="Liabilities" items={data.liabilities} total={data.total_liabilities} totalLabel="Total Liabilities" color="#EF4444" />

              <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm" style={{ borderLeftWidth: 4, borderLeftColor: '#1E40AF' }}>
                <div className="px-4 py-3 border-b border-[#e5e7eb]"><h2 className="font-semibold">Equity</h2></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[400px]">
                  <tbody>
                    {data.equity.map((e, i) => (
                      <tr key={i} className="border-t border-[#e5e7eb]"><td className="py-2 px-4">{e.code} - {e.name}</td><td className="py-2 px-4 text-right font-medium">Rs. {e.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                    ))}
                    <tr className="border-t border-[#e5e7eb]"><td className="py-2 px-4 italic">Net Profit (Current Period)</td><td className="py-2 px-4 text-right font-medium">Rs. {data.net_profit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr>
                  </tbody>
                  <tfoot className="bg-[#f9fafb] font-semibold"><tr><td className="py-2 px-4">Total Equity</td><td className="py-2 px-4 text-right">Rs. {data.total_equity.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td></tr></tfoot>
                </table>
              </div>
            </div>

              {/* Equation Check */}
              <div className="bg-white border-2 border-[#1E40AF] rounded-sm shadow-sm p-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-sm text-[#6b7280]">Total Assets</p>
                    <p className="text-xl font-bold text-[#1E40AF]" data-testid="bs-total-assets">Rs. {data.total_assets.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="flex items-center justify-center text-2xl font-bold text-[#6b7280]">=</div>
                  <div>
                    <p className="text-sm text-[#6b7280]">Liabilities + Equity</p>
                    <p className="text-xl font-bold text-[#1E40AF]" data-testid="bs-total-le">Rs. {(data.total_liabilities + data.total_equity).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default BalanceSheet;
